// Import React state
import { useState } from "react";

// Import hooks
import { useToast } from "global/hooks/useToast";

// Import utilities
import { copyCode } from "global/utilities/Copy";

// Import your existing popup layout
import TutorialLayout from "../../global/components/tutorials/components/TutorialLayout";

// Import the tutorial styles
import "./css/InlineShoppingTutorial.css";

// Import the real example screenshots
import websiteScreenshot from "./assets/InlineShoppingWebsite.png";
import buttonScreenshot from "./assets/InlineShoppingButton.png";
import productsScreenshot from "./assets/InlineShoppingProducts.png";
import galleryScreenshot from "./assets/InlineShoppingGallery.png";
import WordPressLogo from "./assets/WordPressLogo.jpg";
import WebflowLogo from "./assets/WebflowLogo.jpg";
import CreateButton from "global/components/CreateButton";





// Define the supported website platforms
const platforms = [
    { id: "wordpress", label: "WordPress", symbol: "W", image: WordPressLogo},
    { id: "webflow", label: "Webflow", symbol: "W", image: WebflowLogo },
    { id: "custom", label: "Custom HTML / Other", symbol: "</>" }
];



// Define platform-specific guidance for each step
const guidance = {
    wordpress: {
        install: "Use an approved site-wide custom-code feature or your theme's shared layout. Editing theme files directly may be overwritten by updates.",
        image: "Edit the image's HTML if your editor allows it. Some image blocks strip custom attributes, so a Custom HTML block or developer help may be needed.",
        gallery: "Add a Custom HTML block to the page with your artwork and paste the gallery code where the products should appear."
    },
    webflow: {
        install: "Use Project Settings > Custom Code for site-wide installation, or page-level custom code where available. Check your plan and publish the site.",
        image: "Select an image element and use its custom attributes settings to add data-strich-shoppable-image, or use an Embed element if appropriate.",
        gallery: "Open the artwork page in the Designer, add an Embed element and paste the gallery code in the desired position."
    },
    custom: {
        install: "Find your HTML page or shared layout. Add the script inside <head>, near </body>, or in an existing shared footer. The file may be index.html, index.php or a template.",
        image: "Find the image's opening <img> tag and add the shoppable attribute. Keep the existing src, alt, class and other attributes.",
        gallery: "Edit the page containing your shoppable images and place the gallery <div> where the products should appear."
    }
};



// Render a small reusable informational panel
function Notice({ title, children, tone = "info" }) {

    // Render the message with a theme-aware modifier
    return (

        <aside className={`inline-shopping__notice inline-shopping__notice--${tone}`}>

            <strong>{title}</strong>

            <div>{children}</div>

        </aside>
    );

}



// Render code with an accessible format switch and copy action
function CodeExample({ title, code, defaultFormat = "single", showCopy = true, stackable = true }) {

    // Track the current code layout
    const [format, setFormat] = useState(defaultFormat);

    // Track copy confirmation
    const [copied, setCopied] = useState(false);

    // Show success or error feedback using the Portal's existing notifications
	const { toastSuccess, toastError } = useToast();

    // Convert line breaks and indentation into spaces for one-line HTML
    const displayedCode = format === "single" ? code.replace(/\s*\n\s*/g, " ").trim() : code;

    // Copy the currently visible code
    async function handleCopy() {

        // Do not copy empty code
        if (!displayedCode) {

            return;

        }

        try {

            const response = await copyCode("Code", displayedCode, toastSuccess, toastError);
            
            if (response) {

                // Display the confirmation
                setCopied(true);

                // Clear the copied boolean after 3 seconds
                setTimeout(() => {

                    setCopied &&

                        setCopied(false);

                }, 3000);

            }

        } catch {

            // Keep the copy button available after an error
            setCopied(false);

        }

    }


    // Render the code card
    return (

        <div className="inline-shopping__code-card">

            <div className="inline-shopping__code-top">

                <strong>{title}</strong>

                {showCopy && (


                    <CreateButton 
                        title={copied ? "Copied!" : "Copy code"} 
                        size="s"
                        onClick={handleCopy} 
                        disabled={!displayedCode} 
                    />

                )}

            </div>

            {stackable &&

                <div className="inline-shopping__format" role="group" aria-label={`${title} formatting`} >

                    <button type="button" className={format === "single" ? "inline-shopping__format-option inline-shopping__format-option--active" : "inline-shopping__format-option"} aria-pressed={format === "single"} onClick={() => { setFormat("single"); setCopied(false); }}>
                        One line
                    </button>
                
                    <button type="button" className={format === "stacked" ? "inline-shopping__format-option inline-shopping__format-option--active" : "inline-shopping__format-option"} aria-pressed={format === "stacked"} onClick={() => { setFormat("stacked"); setCopied(false); }}>
                        Stacked
                    </button>

                </div>

            }

            <pre className="inline-shopping__code"><code>{displayedCode || "Open your store to generate the installation code"}</code></pre>

        </div>

    );

}



// Render a screenshot with a caption and an enlarge link
function Screenshot({ src, alt, caption }) {

    // Show a useful placeholder when no image is supplied
    if (!src) {

        return <div className="inline-shopping__screenshot-missing">{caption}</div>;

    }

    // Display the image and its caption
    return (

        <figure className="inline-shopping__screenshot">

            <a href={src} target="_blank" rel="noreferrer" title="Open larger image">
                <img src={src} alt={alt} loading="lazy" />
            </a>

            {caption && <figcaption>{caption}</figcaption>}

        </figure>

    );

}



// Render an expandable platform-specific example
function PlatformAccordion({ platform, selected, onSelect, topic }) {

    // Display one website platform with its guidance
    return (

        <div className="inline-shopping__platform-item">

            <button type="button" className="inline-shopping__platform-toggle" aria-expanded={selected === platform.id} onClick={() => onSelect(selected === platform.id ? "" : platform.id)}>
                                
                {platform.image ? <img src={platform.image} alt={platform.label} style={{maxWidth: "38px"}} /> : <span className="inline-shopping__platform-icon">{platform.symbol}</span> }
                <strong>{platform.label}</strong>
                <span aria-hidden="true">{selected === platform.id ? "−" : "+"}</span>
                
            </button>

            {selected === platform.id && (

                <p className="inline-shopping__platform-description">{guidance[platform.id][topic]}</p>

            )}

        </div>

    );

}



// Render the platform help section for the current step
function PlatformGuidance({ topic }) {

    // Track the expanded platform
    const [selected, setSelected] = useState("wordpress");

    // Display supported platforms and a restrictions note
    return (

        <div className="inline-shopping__panel">

            <h3>Where do I add this code?</h3>

            <p>Choose your website platform for a quick starting point</p>

            <div className="inline-shopping__platform-list">

                {platforms.map((platform) => (

                    <PlatformAccordion key={platform.id} platform={platform} selected={selected} onSelect={setSelected} topic={topic} />

                ))}

            </div>

            <p className="inline-shopping__muted">Some third-party platforms, plans and editors restrict custom HTML, JavaScript or external scripts. If code is removed or blocked, check your provider's documentation or ask your website developer</p>
        
        </div>

    );

}



// Render a code-placement illustration without relying on a website builder
function PlacementCard({ title, description, snippet, footer }) {

    // Render a simple HTML editor example
    return (

        <article className="inline-shopping__placement">

            <h4>{title}</h4>

            <p>{description}</p>

            <div className="inline-shopping__editor">

                <div className="inline-shopping__editor-bar"><span>● ● ●</span> Example HTML</div>

                <pre><code>{snippet}</code></pre>

            </div>

            <p className="inline-shopping__placement-footer">{footer}</p>

        </article>

    );

}



// Render the main three-step Inline Shopping tutorial
export default function InlineShoppingTutorial({ storeId, imageTag = "", floatingOnly = false, screenshots = {}, popupTitle, popupSubTitle  }) {

    // Track the active step
    const [step, setStep] = useState(1);

    // Track which setup checks have been completed
    const [checks, setChecks] = useState([]);

    // Use a safe store identifier when constructing HTML
    const safeStoreId = String(storeId ?? "").replace(/[&"<>']/g, "");

    // Generate the real installation script from the store identifier
    const installationCode = safeStoreId ? `<script\n    src="https://assets.strichsuite.com/modules/pod_inline/embed.js"\n    data-strich-store="${safeStoreId}"\n    async\n></script>` : "";

    const imageCode = `data-strich-shoppable-image`;

    // Show a typical existing image
    const beforeCode = `<img\n    src="images/painting.jpg"\n    alt="Landscape painting"\n>`;

    // Show the same image with one additional attribute
    const afterCode = `<img\n    src="images/painting.jpg"\n    alt="Landscape painting"\n    data-strich-shoppable-image\n>`;

    const entireImageTag = `<img\n    src="images/painting.jpg"\n    alt="Landscape painting"\n    data-strich-shoppable-image\n>`;

    // Show how optional attributes can override store defaults
    const overrideCode = `<img\n    src="images/coastal-landscape-painting.jpg"\n    alt="Coastal Landscape Painting"\n    data-strich-shoppable-image\n    data-strich-template="landscape_template"\n    data-strich-coverage="fill"\n    data-strich-pod-print-file="coastal-landscape-painting"\n>`;

    // Define the gallery placement snippet
    const galleryCode = `<div data-strich-product-gallery></div>`;

    // Read screenshots from props or use the included examples
    const images = {
        website: screenshots.website || websiteScreenshot,
        button: screenshots.button || buttonScreenshot,
        products: screenshots.products || productsScreenshot,
        gallery: screenshots.gallery || galleryScreenshot
    };

    // Move to a specific step without leaving the popup
    function goToStep(nextStep) {

        // Keep the selected step within the supported range
        setStep(Math.min(4, Math.max(1, nextStep)));

    }

    // Update the completion checklist
    function toggleCheck(index) {

        // Add or remove the selected check
        setChecks((current) => current.includes(index) ? current.filter((item) => item !== index) : [...current, index]);

    }


    const footerNextBack = <nav className="inline-shopping__navigation" aria-label="Tutorial navigation">
                               
                                <CreateButton type="button" title="← Back" size="m" plain={true} onClick={() => goToStep(step - 1)} disabled={step === 1} />

                                <div className="inline-shopping__dots" aria-label={`Screen ${step} of 4`}>
                                    {[1, 2, 3, 4].map((number) => (
                                        <button type="button" key={number} className={number === step ? "inline-shopping__dot inline-shopping__dot--active" : "inline-shopping__dot"} aria-label={`Go to screen ${number}`} aria-current={number === step ? "step" : undefined} onClick={() => goToStep(number)} />
                                    ))}
                                </div>

                                <CreateButton title={step === 4 ? "Start again" : step === 3 ? "Test setup →" : "Next step →"} size="m" type="button" onClick={() => goToStep(step === 4 ? 1 : step + 1)} />
                            
                            </nav>

    // Render the tutorial inside the existing portal popup
    return (

        <TutorialLayout title={"Add Inline Shopping to your website"} description="Three simple steps to make your images shoppable" minutes={5} steps={3} footer={footerNextBack} setStep={setStep}>

            <div className="inline-shopping">

                <div className="inline-shopping__grid">

                    <div className="inline-shopping__main">

                        <header className="inline-shopping__heading">

                            <span className="inline-shopping__step-number">{step === 4 ? "✓" : step}</span>

                            <div>
                                <h2>{["", "Add StrichSuite to your website", "Make an image shoppable", "Add the product gallery (optional)", "Check your setup"][step]}</h2>
                                <p>{["", "Copy this code into your website. You only need to add it once.", "Add a small piece of code to any image you want visitors to shop from.", "Choose exactly where the embedded products appear on your page.", "Publish your changes and test the shopping experience."][step]}</p>
                            </div>

                        </header>

                        {/* Step one shows the installation script and three placement examples */}
                        {step === 1 && (

                            <>
                                <CodeExample title="Your installation code" code={installationCode} />

                                <Notice title="That's it!" tone="success">
                                    <p>Copy the code, paste it into your website and publish. StrichSuite will load on pages where it is needed</p>
                                </Notice>

                                <section className="inline-shopping__panel">

                                    <h3>Where can I put the code?</h3>

                                    <p>Choose whichever location is easiest to access. For multiple pages, a shared layout or site-wide code area is usually best</p>

                                    <div className="inline-shopping__placement-grid">

                                        <PlacementCard title="Inside the <head>" description="Add the script inside the head of your HTML page" snippet={`<html>\n  <head>\n    <!-- Add StrichSuite here -->\n    <script ...></script>\n  </head>\n  <body>...</body>\n</html>`} footer="Useful for a shared layout" />
                                        <PlacementCard title="Before </body>" description="Place the script near the end of the page" snippet={`<html>\n  <head>...</head>\n  <body>\n    <!-- Your page content -->\n    <script ...></script>\n  </body>\n</html>`} footer="Easy for a single HTML page" />
                                        <PlacementCard title="Custom code area" description="Many websites provide a field for shared scripts" snippet={`Website settings\n  > Custom code\n    > Header or footer\n      [Paste script here]`} footer="Convenient for multiple pages" />
                                    
                                    </div>

                                </section>
                            </>

                        )}

                        {/* Step two demonstrates the image transformation and attributes */}
                        {step === 2 && (

                            <>

                                <section className="inline-shopping__panel">

                                    <h3>What does the shoppable image look like?</h3>
                                    <div className="inline-shopping__visual-steps">
                                        <article><strong>1. Your website image</strong><p>Keep the image already on your website</p><Screenshot src={images.website} alt="Artist portfolio showing existing artwork images" /></article>
                                        <article><strong>2. Add the buy button</strong><p>The button appears when Floating Products is enabled</p><Screenshot src={images.button} alt="Close-up of artwork with a shopping button" /></article>
                                        <article><strong>3. Customers shop</strong><p>Visitors choose products featuring your artwork</p><Screenshot src={images.products} alt="Products available for the selected artwork" /></article>
                                    </div>

                                </section>

                                <section className="inline-shopping__panel">

                                    <h3>Where can I put the code?</h3>
                                    <p>Add the code to an existing image on your website OR use the tools in your stores 'Inline Shopping' settings to create a complete <code>&lt;img&gt;</code> tag which you can paste into your site.</p>
                                    <br/>
                                    <h4>Option 1: Add code to an existing image</h4>                                    
                                    <p>Find its <code>&lt;img&gt;</code> tag and add <b><code>data-strich-shoppable-image</code></b>. Keep the original image URL and other attributes. <b>Image format must be PNG, JPG or JPEG.</b></p>
                                    <CodeExample title="The image code to add" code={imageCode} stackable={false}/>
                                    <div className="inline-shopping__comparison">
                                        <CodeExample title="Before — existing image" code={beforeCode} showCopy={false} />
                                        <CodeExample title="After — shoppable image" code={afterCode} showCopy={false} />
                                    </div>
                                    <br/>
                                    <br/>
                                    <h4>Option 2: Create <code>&lt;img&gt;</code> tag to paste into your site</h4>
                                    <p>Create a complete <code>&lt;img&gt;</code> tag using the tools in your store's Inline Shopping settings. <b>Image format must be PNG, JPG or JPEG.</b></p>
                                    <div className="inline-shopping__comparison">
                                        <CodeExample title="Entire image tag (example)" code={entireImageTag} showCopy={false} />
                                        <PlacementCard title="Add image to your site" description="Put the image anywhere on your website, maybe in a gallery, merch or shop area" snippet={`<main>\n\n  <!-- Your gallery -->\n  <div id="gallery">\n    <img src="painting.jpg"\n       alt="Landscape painting"\n       data-strich-shoppable-image\n    >\n  </div>\n  \n</main>`} footer="Add as many shoppable images as you like" />
                                    </div>



                                </section>

                                <section className="inline-shopping__panel">

                                    <h3>Optional image settings</h3>
                                    <p>Only add these attributes when you want to override your store defaults for an individual image.</p>
                                    <div className="inline-shopping__setting-list">
                                        <div><code>data-strich-template</code><span>Choose the products offered, using a predefined or your own custom template.</span></div>
                                        <div><code>data-strich-coverage</code><span>Control how artwork fits on printable products, either <b>fit</b> within the print area (no cropping) or <b>fill</b> the print area (may crop the image).</span></div>
                                        <div><code>data-strich-pod-print-file</code><span>Link a separate high-resolution image from your StrichSuite image bucket, for printing purposes only, <b>ensuring the linked image matches the shoppable image</b> on your site.</span></div>
                                    </div>
                                    <CodeExample title="Example with optional settings" code={overrideCode} showCopy={false} />

                                </section>

                                <section className="inline-shopping__panel">

                                    <h3>Better print quality, without slowing your website</h3>
                                    <p>Website images are often small or compressed. Upload the full-quality artwork to your StrichSuite image bucket, then enter its image name in the High-resolution print file field.</p>
                                    <div className="inline-shopping__print-flow">
                                        <div><span aria-hidden="true">▣</span><strong>Website image</strong><small>Small, fast-loading preview</small></div>
                                        <span aria-hidden="true">→</span>
                                        <div><span aria-hidden="true">▤</span><strong>StrichSuite image bucket</strong><small>Full-quality artwork for production</small></div>
                                    </div>
                                    <p>Your website image stays unchanged. The linked high-resolution file is used for producing orders.</p>

                                </section>

                            </>

                        )}

                        {/* Step three explains embedded galleries and shows their placement */}
                        {step === 3 && (

                            <>

                                <section className="inline-shopping__panel">

                                    <h3>What does the embedded gallery look like?</h3>
                                    <p>When Floating Products is disabled, shoppable images and their products appear in an embedded gallery on the page.</p>

                                    {images.gallery ? (

                                        <Screenshot src={images.gallery} alt="Embedded product gallery on an artwork page" caption="Example embedded gallery" />

                                    ) : (

                                        <div className="inline-shopping__gallery-demo">
                                            <div className="inline-shopping__gallery-site"><strong>Nora Lark Studio</strong><span>Home　 Gallery　 About</span></div>
                                            <h4>Coastal Weather</h4>
                                            <div className="inline-shopping__gallery-art"><img src={images.button} alt="Artwork with the shopping button shown in the floating example" /><p>Shoppable artwork on the page</p></div>
                                            <div className="inline-shopping__gallery-products"><h4>Products featuring this artwork</h4><Screenshot src={images.products} alt="Example product selection illustrating the products in a gallery" /></div>
                                        </div>

                                    )}

                                </section>

                                <CodeExample title="The gallery code to add" code={galleryCode} stackable={false}/>

                                <section className="inline-shopping__panel">

                                    <h3>Where can I put the code?</h3>
                                    <p>Add the code where you want the products to appear, ideally below your artwork. It must be on the same page as the shoppable images.</p>
                                    <PlacementCard title="Example: below your images" description="Put the gallery after the section containing your artwork" snippet={`<main>\n  <!-- Your shoppable images -->\n  <img src="painting.jpg"\n       data-strich-shoppable-image\n  >\n\n  <div data-strich-product-gallery></div>\n</main>`} footer="Add once on each page where you want an embedded gallery" />
                                                                
                                </section>

                            </>

                        )}

                        {/* Final screen gives the user a quick functional test */}
                        {step === 4 && (

                            <section className="inline-shopping__panel">

                                <h3>Quick final checklist</h3>

                                {[
                                    "I added the installation script once",
                                    "I added the shoppable attribute to my chosen images",
                                    "My store settings and product templates are correct",
                                    "If 'Floating Products' setting is off, the gallery is in a suitable place on the same page as my images",
                                    "I published the changes and tested the shopping experience"
                                ].map((item, index) => (

                                    <label className="inline-shopping__check" key={item}>
                                        <input type="checkbox" checked={checks.includes(index)} onChange={() => toggleCheck(index)} style={{width: "auto"}} />
                                        <span>{item}</span>
                                    </label>

                                ))}

                                <br/>

                                <Notice title="Need help?" tone="info"><p>If nothing appears, check your store identifier, confirm the page was published and verify that your website allows third-party scripts</p></Notice>
                            
                            </section>

                        )}

                    </div>

                    {/* Keep the explanatory side panels visible beside the current step */}
                    <aside className="inline-shopping__sidebar">

                        {step === 1 && (

                            <>
                                <Notice title="Important information" tone="warning"><ul><li>Add the script only once to your site</li><li>Use a shared header, footer or layout for multiple pages</li><li>Use a page-specific location for just one page</li><li>One-line and stacked HTML work the same way</li></ul></Notice>
                                <Notice title="Removing StrichSuite" tone="danger"><p>If you ever want to remove StrichSuite from your website, simply remove the installation script from your site and publish the change. That's it - it will no longer load.</p></Notice>
                                <PlatformGuidance topic="install" />
                            </>

                        )}

                        {step === 2 && (

                            <>
                                <Notice title="About the buy button" tone="info"><p>The shopping button appears on the image only when <i>Floating Products</i> is enabled in your store settings. Clicking it opens the product selection.</p></Notice>
                                <Notice title="Important" tone="warning"><ul><li>You can make any supported website image shoppable</li><li>Image formats must be either PNG, JPG or JPEG</li><li>Keep the existing image URL, alt text and attributes</li><li>Optional settings override store defaults</li><li>If the <i>Floating Products</i> setting is off, complete Step 3 to position the gallery</li></ul></Notice>
                                <div className="inline-shopping__panel"><h3>Generate a complete image tag</h3><p>In your StrichSuite portal, enter an image URL and alt text, choose any overrides, then copy the generated tag.</p>{imageTag && <CodeExample title="Your generated image tag" code={imageTag} />}<p className="inline-shopping__muted">If the image is already on your site, you can simply edit its existing tag instead.</p></div>
                                <PlatformGuidance topic="image" />
                            </>

                        )}

                        {step === 3 && (

                            <>
                                <Notice title="When do I need this code?" tone="danger"><p>Only when <i>Floating Products</i> is disabled. If it is enabled, the shopping button appears on images and no embedded gallery code is needed.</p></Notice>
                                <Notice title="What if I don't add it?" tone="info"><p>Without a gallery placeholder, the embedded gallery may appear in an unexpected position on your page.</p></Notice>
                                <Notice title="Important" tone="warning"><ul><li>The gallery must be on the same page as the shoppable images</li><li>Add it once per page, wherever you want products displayed</li><li>Avoid site-wide footers unless you deliberately want that position on every page</li></ul></Notice>
                                <PlatformGuidance topic="gallery" />
                            </>

                        )}

                        {step === 4 && <Notice title="You're ready" tone="success"><p>Once everything is checked, your visitors can shop artwork directly from your website.</p></Notice>}

                    </aside>

                </div>

            </div>

        </TutorialLayout>

    );

}
